import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { useDispatch } from "react-redux";
import { mapErrors } from "../../../Utils";
import { PMDialog } from "../../../utils/PMDialog";
import {
  createVariableTypeAsync,
  updateVariableTypeAsync,
} from "../../../../slices/service/variable-types";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { fetchVariableType } from "../../../../api/service/settings";

export default function ServiceVariableTypeDialog(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { open, onClose, itemId } = props;

  const [itemState, setItemState] = useState({ isLoading: false, variable_type: null });

  const { variable_type, isLoading } = itemState;

  const getItem = async (id) => {
    setItemState((prev) => ({ ...prev, isLoading: true }));
    const res = await fetchVariableType(id);
    if (res?.error) {
      setItemState((prev) => ({ ...prev, isLoading: false }));
      toast.error(t("Failed to load variable type"));
      return;
    }
    setItemState({ isLoading: false, variable_type: res });
  };

  useEffect(() => {
    if (open && itemId) {
      getItem(itemId);
    } else if (open) {
      setItemState({ isLoading: false, variable_type: null });
    }
  }, [open, itemId]);

  const createOrUpdateDispatch = (data) => {
    if (variable_type?.id) {
      return dispatch(updateVariableTypeAsync(data));
    }
    return dispatch(createVariableTypeAsync(data));
  };

  return (
    <PMDialog
      title={itemId ? t("service.variable_types.edit") : t("service.variable_types.create")}
      open={open}
      onClose={onClose}
    >
      {isLoading ? (
        <ResourceLoading />
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            id: variable_type?.id || null,
            title: variable_type?.title || "",
            description: variable_type?.description || "",
            submit: null,
          }}
          validationSchema={Yup.object({
            title: Yup.string().max(255).required(t("title.required")),
            description: Yup.string().max(500),
          })}
          onSubmit={(values, helpers) => {
            try {
              toast.promise(createOrUpdateDispatch(values), {
                loading: t("Saving...."),
                success: (data) => {
                  if (data.status === 500) throw new Error("Server error");
                  if (data.payload?.errors)
                    throw new Error(mapErrors(data.payload.errors));
                  onClose(true);
                  return t("Data was saved!");
                },
                error: t("Something went wrong. Could not save data!"),
              });
              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
            } catch (err) {
              toast.error("Something went wrong...");
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => (
            <Form>
              <div className="grid grid-cols-6 gap-6 mt-6">
                <div className="col-span-6 sm:col-span-3">
                  <PMField name="title" type="text" label={t("Title")} {...formik} />
                </div>
                <div className="col-span-6 sm:col-span-6">
                  <PMField
                    name="description"
                    type="text"
                    label={t("Description")}
                    multiline
                    rows={2}
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  type="button"
                  className="rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm"
                  onClick={onClose}
                >
                  {t("Close")}
                </button>
                <button
                  type="button"
                  className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm"
                  onClick={formik.submitForm}
                >
                  {variable_type?.id ? t("Save") : t("Create")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </PMDialog>
  );
}


